import { GwDatesWrapper } from '../components/_internal_date_/gwDatesWrapper';
import { DATE_FORMATS } from './constants';

/**
 *
 * @param {Date} date to be converted
 * @returns Date in the format MM/DD
 */
export const getDateMonthDayFormat = date => {
  return date && GwDatesWrapper.format(date, DATE_FORMATS.monthDayFormat);
};

/**
 * Number of nights between two stay dates. Wraps the diff so callers cannot trip over its
 * (later, earlier) argument order.
 * @param {Date|string} arrival
 * @param {Date|string} departure
 * @returns {number} Night count, or 0 when either date is missing.
 */
export const getNightsCount = (arrival, departure) => {
  return arrival && departure ? GwDatesWrapper.diff(departure, arrival, 'days') : 0;
};

/**
 *
 * @param {Date} date to be converted
 * @returns Date in the format 'MMM. D'
 */
export const getMonthLongDayFormat = date => {
  return date && GwDatesWrapper.format(date, DATE_FORMATS.formatMonthDate);
};

/**
 * @param {Date | string} date to be converted
 * @returns Date in the format Month. DD, YYYY
 */
export const getMonthLetterDayYearFormat = date => {
  if (!date) {
    return '';
  }

  if (typeof date === 'string') {
    return GwDatesWrapper.format(date, DATE_FORMATS.monthLetterDayYear);
  }

  return GwDatesWrapper.format(date, DATE_FORMATS.monthLetterDayYear);
};

export const getMonthAbbrevFormat = date => {
  return date && GwDatesWrapper.format(date, DATE_FORMATS.monthAbbrev);
};

export const isDateValid = dateStr => {
  return !isNaN(new Date(dateStr));
};

const YEARLESS_TYPED_DATE_FORMATS = ['MMMM D', 'MMMM DD', 'MMM D', 'MMM DD', 'M/D', 'M/DD', 'MM/DD'];

const MAX_YEARS_ROLLED_FORWARD = 2;

const titleCaseWords = value => value.replace(/[a-z]+/gi, word => word[0].toUpperCase() + word.slice(1).toLowerCase());

const parseAgainstYearlessFormats = typedValue => {
  for (const format of YEARLESS_TYPED_DATE_FORMATS) {
    const parsed = GwDatesWrapper.parseDateExact(typedValue, format);
    if (parsed) return parsed;
  }

  return null;
};

const rollYearForwardTo = (date, notBefore) => {
  let candidate = date;

  for (let i = 0; i < MAX_YEARS_ROLLED_FORWARD && GwDatesWrapper.isBefore(candidate, notBefore); i++) {
    candidate = GwDatesWrapper.add(candidate, 1, 'year');
  }

  return candidate;
};

/**
 * Resolves a date the guest typed without a year, e.g. 'January 15', 'Jan 15' or '1/15'.
 * The year defaults to the current one and is rolled forward until the date is no longer
 * before `notBefore`, so 'January 15' typed in August 2026 resolves to 2027-01-15.
 *
 * Anything carrying a year returns null on purpose: strict parsing rejects it against
 * every year-less format, leaving those strings to react-dates' own parsing.
 *
 * @param {string} typedValue raw text from the date input
 * @param {string} notBefore earliest acceptable date, in the standard YYYY-MM-DD format
 * @returns {string|null} the resolved date as YYYY-MM-DD, or null when the text is not a
 *   year-less date
 */
export const parseYearlessTypedDate = (typedValue, notBefore) => {
  if (typeof typedValue !== 'string') return null;

  const normalized = titleCaseWords(typedValue.trim().replace(/\s+/g, ' '));

  if (!normalized) return null;

  const parsed = parseAgainstYearlessFormats(normalized);

  if (!parsed) return null;

  return rollYearForwardTo(parsed, notBefore);
};
